import type {
  InboxWorkflowJson,
  InboxWorkflowStepConfig,
  InboxWorkflowTableColumn,
  InboxWorkflowTableFilterConfig,
  InboxWorkflowTableRuntimeRow,
  InboxWorkflowTableStepConfig,
} from "./types"
import type { TableFilterValues } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"

export const INBOX_WORKFLOW_ROW_NUMBER_COLUMN_ID = "__inboxWorkflowRowNumber"

export type InboxWorkflowRowNumberState = {
  nextNumber: number
  byIdentity: Record<string, number>
}

export const inboxWorkflowObjectValue = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

export const inboxWorkflowArrayObjects = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.map(inboxWorkflowObjectValue).filter((entry) => Object.keys(entry).length)
    : []

export const inboxWorkflowStringValue = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

export const inboxWorkflowColumn = <
  Row extends InboxWorkflowTableRuntimeRow = InboxWorkflowTableRuntimeRow,
  Draft extends InboxWorkflowJson = InboxWorkflowJson,
>(
  id: string,
  label: string,
  cellKey: string,
  extra: Omit<Partial<InboxWorkflowTableColumn<Row, Draft>>, "id" | "label" | "cellKey"> = {},
): InboxWorkflowTableColumn<Row, Draft> => ({
  id,
  label,
  cellKey,
  ...extra,
})

export const inboxWorkflowStatusColumn = <
  Row extends InboxWorkflowTableRuntimeRow = InboxWorkflowTableRuntimeRow,
  Draft extends InboxWorkflowJson = InboxWorkflowJson,
>(): InboxWorkflowTableColumn<Row, Draft> =>
  inboxWorkflowColumn<Row, Draft>("status", "Status", "status")

export const inboxWorkflowRuntimeRows = <Row extends { actionCaseId: string }>(
  rows: Row[],
): InboxWorkflowTableRuntimeRow[] =>
  rows.map((row) => ({
    ...row,
    id: row.actionCaseId,
  })) as unknown as InboxWorkflowTableRuntimeRow[]

export const createInboxWorkflowRowNumberState = (): InboxWorkflowRowNumberState => ({
  nextNumber: 1,
  byIdentity: {},
})

export const inboxWorkflowRowNumberIdentity = (row: InboxWorkflowTableRuntimeRow): string =>
  inboxWorkflowStringValue(row.id, row.actionCaseId)

export const applyInboxWorkflowRowNumbers = <Row extends InboxWorkflowTableRuntimeRow>(
  rows: Row[],
  state: InboxWorkflowRowNumberState,
  columnId = INBOX_WORKFLOW_ROW_NUMBER_COLUMN_ID,
): Array<Row & { [INBOX_WORKFLOW_ROW_NUMBER_COLUMN_ID]?: number }> => {
  const nextByIdentity = { ...state.byIdentity }
  let nextNumber = state.nextNumber

  const numberedRows = rows.map((row, index) => {
    const identity = inboxWorkflowRowNumberIdentity(row) || `row:${index}`
    const rowNumber = nextByIdentity[identity] ?? nextNumber++
    nextByIdentity[identity] = rowNumber

    return {
      ...row,
      [columnId]: rowNumber,
    }
  })

  state.byIdentity = nextByIdentity
  state.nextNumber = nextNumber

  return numberedRows
}

export const inboxWorkflowPartialMessage = (result: { success_count: number; rows: unknown[] }) =>
  `Schritt teilweise ausgeführt (${result.success_count}/${result.rows.length}).`

export type InboxWorkflowPreconfigurationTargetResolution<Row> = {
  hasSelection: boolean
  targetRows: Row[]
  skippedRows: Row[]
}

export const resolveInboxWorkflowPreconfigurationTargets = <Row>({
  rows,
  selectedIds,
  getId,
  isEligible = () => true,
}: {
  rows: Row[]
  selectedIds?: string[] | Set<string> | null
  getId: (row: Row) => string
  isEligible?: (row: Row) => boolean
}): InboxWorkflowPreconfigurationTargetResolution<Row> => {
  const selected =
    selectedIds instanceof Set ? selectedIds : new Set((selectedIds ?? []).map(String))
  const scopedRows = selected.size ? rows.filter((row) => selected.has(getId(row))) : rows
  const targetRows: Row[] = []
  const skippedRows: Row[] = []

  for (const row of scopedRows) {
    if (isEligible(row)) {
      targetRows.push(row)
    } else {
      skippedRows.push(row)
    }
  }

  return {
    hasSelection: selected.size > 0,
    targetRows,
    skippedRows,
  }
}

const safeCount = (value: number) => Math.max(0, Math.floor(Number.isFinite(value) ? value : 0))

export const notifyInboxWorkflowPreconfigurationApplyResult = ({
  total,
  applied,
  skipped,
  reason,
}: {
  total: number
  applied: number
  skipped?: number
  reason?: string
}) => {
  const safeTotal = safeCount(total)

  if (safeTotal <= 0) return

  const safeApplied = Math.min(safeCount(applied), safeTotal)
  const safeSkipped =
    skipped === undefined ? safeTotal - safeApplied : Math.min(safeCount(skipped), safeTotal)

  if (safeSkipped <= 0) return

  const reasonText = reason?.trim() ? ` ${reason.trim()}` : ""
  newNotification({
    class: "warning",
    html: `Vorkonfiguration konnte bei ${safeSkipped} von ${safeTotal} Zeilen nicht angewandt werden.${reasonText}`,
  })
}

export const composeInboxWorkflowFlowSteps = (
  entries: Record<string, InboxWorkflowTableStepConfig | InboxWorkflowStepConfig>,
) => entries as Record<string, InboxWorkflowStepConfig>

export const DEFAULT_INBOX_WORKFLOW_FILTERS: NonNullable<
  InboxWorkflowTableFilterConfig["definitions"]
> = [
  {
    id: "search",
    label: "Suche",
    placeholder: "Suche",
    type: "text",
  },
  {
    id: "row_status",
    label: "Status",
    type: "select",
    options: [
      { value: "all", label: "Alle" },
      { value: "open", label: "Offen" },
      { value: "resolved", label: "Erledigt" },
      { value: "failed", label: "Fehler" },
      { value: "disabled", label: "Gesperrt" },
    ],
    defaultValue: "all",
    brightBackground: true,
  },
]

const rowSearchText = (row: InboxWorkflowTableRuntimeRow): string => {
  const parts: string[] = [
    row.statusLabel ?? "",
    row.statusDetail ?? "",
    row.statusHint ?? "",
    row.actionCaseId,
    row.searchText ?? "",
  ]

  return parts.join(" ").toLowerCase()
}

const statusMatches = (row: InboxWorkflowTableRuntimeRow, status: string): boolean => {
  if (!status || status === "all") return true

  if (status === "failed") return row.rowOutcome?.status === "failed"

  if (status === "resolved") return row.isResolved === true

  if (status === "open") return row.isOpen !== false && row.disabled !== true

  if (status === "disabled") return row.disabled === true

  return true
}

export const applyDefaultInboxWorkflowFilters = <Row extends InboxWorkflowTableRuntimeRow>(
  rows: Row[],
  filters: TableFilterValues,
): Row[] => {
  const search = inboxWorkflowStringValue(filters.search).toLowerCase()
  const status = inboxWorkflowStringValue(filters.row_status) || "all"

  return rows.filter((row) => {
    if (search && !rowSearchText(row).includes(search)) return false

    return statusMatches(row, status)
  })
}
